import { GameRarity } from './rarity';

export type GachaBundle = Pick<IBundle, 'id' | 'name'>;

export interface IGachaBannerCashback {
    currency: string;
    amount: number;
}

export interface IGachaBannerImages {
    big: string;
    mobile: Nullable<string>;
}

export enum GachaBannerGroupRarity {
    common = 'common',
    valuable = 'valuable',
    supreme = 'supreme',
}

export type GachaBannerRewardCompensation = Record<string, number>;

export interface IGachaBannerInvoiceItem extends IItemCommonData {
    isPrimary?: boolean;
    compensation?: Nullable<GachaBannerRewardCompensation>;
}

export interface IGachaBannerRewardImages {
    medium: string;
    big: string;
    small: string;
}

export interface IGachaBannerDefaultReward {
    name: string;
    title: string;
    items: IGachaBannerInvoiceItem[];
    images: IGachaBannerRewardImages;
    backgroundColor?: string;
    cashback?: Nullable<IGachaBannerCashback>;
    introVideo?: IVideo;
    idleVideo?: IVideo;
    decoration: string[];
    additionalDescription?: string;
}

export interface IGachaBannerReward extends IGachaBannerDefaultReward {
    probability: number;
    isExtraReward: boolean; // FE field
    rarity: GameRarity;
}

export interface IGachaBannerRewardsSubGroup {
    title: string;
    description: Nullable<string>;
    savePoint: number;
    name: string;
    rarity: GachaBannerGroupRarity;
    rewardBundles: IGachaBannerReward[];
}

export interface IGachaBannerRewardsGroup {
    name: string;
    title: string;
    description: Nullable<string>;
    probability: number;
    savePoint: number;
    isAllowedCompensation: boolean;
    rarity: GachaBannerGroupRarity; // fe field
    subgroups: IGachaBannerRewardsSubGroup[];
}

export interface IPurchaseConfig {
    amount: number;
    price: number;
    discount: Nullable<number>;
}

export interface IGachaVideoAnimation {
    idle: IVideo;
    common: IVideo;
    epic: IVideo;
    legendary: IVideo;
    rare: IVideo;
    uncommon: IVideo;
}

export interface IGachaBanner {
    name: string;
    title: string;
    description: string;
    rewardsPreview: IGachaBannerInvoiceItem[];
    currency: string;
    cashback: string;
    images: IGachaBannerImages;
    videoAnimation: IGachaVideoAnimation;
    purchaseConfigs: IPurchaseConfig[];
    freeRewards: IGachaBannerRewardsGroup;
    rewardsGroups: IGachaBannerRewardsGroup[];
}

export type GachaCounterState = Record<string, number>;
export type GachaRecievedRewardsState = string[];
export type GachaCompensatedItemIds = number[];

export interface IGachaCurrentFreeReward {
    name: string;
    currentProgress: number;
}

export type GachaAccountFreeRewardState = Record<string, number>;

export interface IGachaAccountState {
    timers: GachaCounterState;
    currentFreeReward: GachaAccountFreeRewardState;
    receivedRewards: GachaRecievedRewardsState;
    compensatedItems: GachaCompensatedItemIds;
    probabilities: IGachaUpdatedProbabilities;
}

export type GachaAccountState = Record<string, IGachaAccountState>;
export interface IGachaAccountStateResponse {
    state: GachaAccountState;
    ts: number;
}

export enum TransactionStatus {
    pending = 'pending',
    success = 'success',
    error = 'error',
}

export interface IGachaPendingTransaction {
    status: TransactionStatus.pending;
    ts: number;
}

interface IGachaProbabilitiesRewardGroup {
    name: string;
    probability: number;
    subgroups: {
        name: string;
        probability: number;
        rewardBundles: {
            name: string;
            probabilities: number;
        }[];
    }[];
}

export interface IGachaUpdatedProbabilities {
    rewardsGroups: IGachaProbabilitiesRewardGroup[];
}

export interface IGachaRollSuccess {
    status: TransactionStatus.success;
    bundleName: string;
    rewards: string[];
    compensatedItems: GachaCompensatedItemIds;
}

export interface IGachaRollError {
    status: TransactionStatus.error;
    bundleName: string;
}

export type GachaTransactionResponse = IGachaRollSuccess | IGachaRollError | IGachaPendingTransaction;

export interface IGachaPurchaseResponse {
    transactionId: string;
}

export enum AnimationSpinesNames {
    IDLE = 'idle',
    COMMON_ROLL = 'common_roll',
    VALUABLE_ROLL = 'valuable_roll',
    LEGENDARY_ROLL = 'legendary_roll',
}
